// MACHINERY V2 (Stroje) — the group header, shared by the grouped-row view and
// the V3 table (extracted from FleetGroupSection.tsx at M2.13).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.3 (§9 D4) + M2.13.
//
// >>> WHY THIS IS ITS OWN FILE NOW. <<< M2.3's rule is "THIS IS THE ONLY SECTION
// DESIGN" — every carried grouping dimension renders through one header, because
// inventing a per-dimension variant is exactly the divergence D4 forbids. M2.13
// adds a THIRD consumer (the table view's per-type group header, which the
// artboard draws identically to V2's). Copying the header into fleet/table/
// would have created the second section design D4 rules out, so the button was
// lifted here verbatim and FleetGroupSection now composes it. Nothing about the
// rendering changed in the move.
//
// HEALTH COUNTS COME FROM THE CONTRACT, NOT FROM TELEMETRY. `counts` is produced
// by fleetSelectors.countStates over states resolved by deriveCardPresentation,
// so a header can never disagree with the badges beneath it. This component
// never sees a raw telemetry field.
//
// The artboard always renders the running + idle chips and shows the rest only
// when non-zero — kept, expressed over the canonical state vocabulary
// (fleetStatusFilters.FLEET_STATE_FILTERS) so the header, the strip and the rail
// can never offer different state names.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2CardState } from "./cardStates";
import { FLEET_STATE_FILTERS, filterInk } from "./fleetStatusFilters";

/** States whose chip renders even at zero — the artboard's "always visible"
 *  pair. Everything else appears only when it has members. */
const ALWAYS_SHOWN: MachineryV2CardState[] = ["running", "idle"];

const SummaryChip: React.FC<{ n: number; ink: string; label: string }> = ({ n, ink, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
    <span style={{ width: 6, height: 6, borderRadius: 999, background: ink, flexShrink: 0 }} />
    <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{n}</span>
    <span style={{ color: "var(--faint)" }}>{label}</span>
  </span>
);

export interface FleetGroupHeaderProps {
  title: string;
  /** DISTINCT machines in this section — the count pill. */
  machineCount: number;
  /** countStates() output for this section's machines. */
  counts: Record<string, number>;
  collapsed: boolean;
  onToggle: () => void;
  /** The table view attaches the header to the card below it. */
  marginBottom?: number;
}

const FleetGroupHeader: React.FC<FleetGroupHeaderProps> = ({
  title, machineCount, counts, collapsed, onToggle, marginBottom = 10,
}) => {
  const { translate } = useTranslation("myMachinery");

  const chips = FLEET_STATE_FILTERS.filter(
    (s) => (counts[s.state] || 0) > 0 || ALWAYS_SHOWN.indexOf(s.state) >= 0
  );

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "12px 16px", border: 0, borderLeft: "3px solid var(--mod-accent)",
        borderRadius: 8, background: "var(--mod-accent-soft)", cursor: "pointer",
        fontFamily: "inherit", textAlign: "left", marginBottom, flexWrap: "wrap",
      }}
    >
      <span style={{ color: "var(--mod-accent)", fontSize: 12 }}>
        {collapsed ? "▸" : "▾"}
      </span>
      <span
        style={{
          fontWeight: 600, color: "var(--ink)", fontSize: 14.5, minWidth: 0,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}
      >
        {title}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--mod-accent)",
          background: "var(--card)", padding: "2px 8px", borderRadius: 999,
          border: "1px solid var(--mod-accent-line)", flexShrink: 0,
        }}
      >
        {translate("myMachinery:v2.fleet.machineCount", { count: machineCount })}
      </span>
      <span style={{ flex: 1, minWidth: 12 }} />
      <span
        style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)",
        }}
      >
        {chips.map((s) => (
          <SummaryChip
            key={s.state}
            n={counts[s.state] || 0}
            ink={filterInk(s.token)}
            label={translate(s.labelKey)}
          />
        ))}
      </span>
    </button>
  );
};

export default FleetGroupHeader;
